import {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import { Popup as AntdMobilePopup } from "antd-mobile";
import { useLocation } from "react-router-dom";
import { useUserStore } from "@/modules/user/stores";
import { openLoginRegisterPopup } from "@/components/LoginRegisterPopup";
import { SetLoginPasswordDialog } from "@/components/SetLoginPasswordDialog";
import {
  WITHDRAW_POPUP_EVENT,
  CLOSE_WITHDRAW_POPUP_EVENT,
} from "./triggerWithdrawPopup";
import type { WithdrawPopupProps } from "../../WithdrawPopup.types";
import ContentSkeleton from "@/components/ContentSkeleton/ContentSkeleton";
import { useT } from "@/i18n/hooks";
import "./WithdrawPopup.scss";

const LazyWithdraw = lazy(() => import("@/pages/Withdraw/Withdraw"));

// prime variant（源自 release-themes-az-sa）：结构同 card 弹窗壳，配色走语义 token 跟随 palette（blossom 浅色）
export function WithdrawPopup({ className = "" }: WithdrawPopupProps) {
  const t = useT();
  const location = useLocation();
  const isAuthenticated = useUserStore(state => state.isAuthenticated());
  const userInfo = useUserStore(state => state.userInfo);
  const loadUserInfo = useUserStore(state => state.loadUserInfo);
  const [visible, setVisible] = useState(false);
  const [key, setKey] = useState(0);
  const openedAtPathRef = useRef<string>("");
  const [setLoginPasswordVisible, setSetLoginPasswordVisible] = useState(false);

  const openWithdrawPopupInternal = useCallback(() => {
    openedAtPathRef.current = window.location.pathname;
    setKey(prev => prev + 1);
    setVisible(true);
  }, []);

  const handleOpen = useCallback(() => {
    if (!isAuthenticated) {
      openLoginRegisterPopup({ defaultTab: "password" });
      return;
    }

    if (userInfo?.hasLoginPassword === false) {
      setSetLoginPasswordVisible(true);
      return;
    }

    openWithdrawPopupInternal();
  }, [isAuthenticated, userInfo?.hasLoginPassword, openWithdrawPopupInternal]);

  const handleSetLoginPasswordSuccess = useCallback(async () => {
    await loadUserInfo();
    setSetLoginPasswordVisible(false);
    openWithdrawPopupInternal();
  }, [loadUserInfo, openWithdrawPopupInternal]);

  const handleSetLoginPasswordClose = useCallback(() => {
    setSetLoginPasswordVisible(false);
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    window.addEventListener(WITHDRAW_POPUP_EVENT, handleOpen);
    window.addEventListener(CLOSE_WITHDRAW_POPUP_EVENT, handleClose);
    return () => {
      window.removeEventListener(WITHDRAW_POPUP_EVENT, handleOpen);
      window.removeEventListener(CLOSE_WITHDRAW_POPUP_EVENT, handleClose);
    };
  }, [handleOpen, handleClose]);

  useEffect(() => {
    if (
      visible &&
      openedAtPathRef.current &&
      location.pathname !== openedAtPathRef.current
    ) {
      setVisible(false);
    }
  }, [location.pathname, visible]);

  return (
    <>
      <AntdMobilePopup
        visible={visible}
        onMaskClick={handleClose}
        position="bottom"
        destroyOnClose
        className={`withdraw-popup ${className}`}
        maskClassName="withdraw-popup__mask"
        bodyClassName="withdraw-popup__body"
        bodyStyle={{
          height: "80vh",
          minHeight: "80vh",
          maxHeight: "80vh",
          width: "100%",
          boxSizing: "border-box",
          borderRadius: "1rem 1rem 0 0",
        }}
      >
        <div className="withdraw-popup__inner">
          <div className="withdraw-popup__sheet">
            <div className="withdraw-popup__drag-bar">
              <span className="withdraw-popup__drag-indicator" />
            </div>

            <div className="withdraw-popup__header">
              <span className="withdraw-popup__title">
                {t("withdraw.title")}
              </span>
              <button
                type="button"
                className="withdraw-popup__close"
                onClick={handleClose}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="withdraw-popup__scroll">
              <Suspense fallback={<ContentSkeleton />}>
                <LazyWithdraw
                  key={key}
                  hideWalletInfo={false}
                  showAccountSelector
                />
              </Suspense>
            </div>
          </div>
        </div>
      </AntdMobilePopup>

      <SetLoginPasswordDialog
        visible={setLoginPasswordVisible}
        onClose={handleSetLoginPasswordClose}
        onSuccess={handleSetLoginPasswordSuccess}
      />
    </>
  );
}
